import { applicationsClient } from '@terra/shared-rest-clients-applications';
import { useProxyStore } from '../store/proxy-store';
import { useProjectStore } from '../store/projectStore';
import { getAuthHeaders } from './authHeaders';
import { getCurrentGatewayUrl } from './environmentDetection';
import { mapBrowserMatchReplaceRuleToLocal } from './match-replace-rule.mapper';
import {
  captureMatchReplacePersistSnapshot,
  type MatchReplacePersistSnapshot,
} from './match-replace-persistence';
import { formatTargetScopeKey, TARGET_TYPE } from './target-scope.utils';

export async function persistMatchReplaceSnapshotToServer(
  snapshot: MatchReplacePersistSnapshot,
  options?: { skipSelectedApplicationCheck?: boolean },
): Promise<{ success: true } | { success: false; error: string }> {
  useProxyStore.getState().setMatchReplacePersistStatus({
    status: 'saving',
    error: null,
  });

  try {
    const authHeaders = await getAuthHeaders();
    const getRes = await applicationsClient.getApplication({
      params: { id: snapshot.applicationId },
      overrideClientOptions: {
        baseUrl: `${getCurrentGatewayUrl()}/orchestrator`,
      },
      extraHeaders: authHeaders,
    });

    if (getRes.status !== 200) {
      throw new Error('Failed to load application before saving rules');
    }

    const currentTargetId = useProjectStore.getState().selectedTarget?.id;
    if (
      !options?.skipSelectedApplicationCheck &&
      currentTargetId !== snapshot.applicationId
    ) {
      if (useProxyStore.getState().matchReplacePersistStatus === 'saving') {
        useProxyStore.getState().setMatchReplacePersistStatus({
          status: 'idle',
        });
      }
      return {
        success: false,
        error: 'Application changed before match/replace rules could be saved',
      };
    }

    const prevHttp = getRes.body.browserConfig?.httpInterceptionRules ?? [];
    const updateRes = await applicationsClient.updateApplication({
      params: { id: snapshot.applicationId },
      body: {
        browserConfig: {
          httpInterceptionRules: prevHttp,
          matchReplaceRules: snapshot.rules,
        },
      },
      overrideClientOptions: {
        baseUrl: `${getCurrentGatewayUrl()}/orchestrator`,
      },
      extraHeaders: authHeaders,
    });

    if (updateRes.status !== 200) {
      throw new Error('Failed to save match/replace rules');
    }

    const currentApp = useProjectStore.getState().selectedTarget;
    if (currentApp?.id === snapshot.applicationId) {
      useProjectStore.getState().updateSelectedTarget({
        ...currentApp,
        browserConfig: {
          httpInterceptionRules: prevHttp,
          matchReplaceRules: snapshot.rules,
        },
      });
    }

    const localRules = snapshot.rules.map(mapBrowserMatchReplaceRuleToLocal);
    useProxyStore.getState().replaceMatchReplaceRulesForApplication(localRules);
    useProxyStore.getState().applyMatchReplaceEnabledForTarget({
      targetScopeKey: formatTargetScopeKey({
        targetId: snapshot.applicationId,
        targetType:
          useProjectStore.getState().selectedTarget?.targetType ??
          useProjectStore.getState().targetType ??
          TARGET_TYPE.WEB_APPLICATION,
      }),
      enabled: snapshot.matchReplaceEnabled,
    });

    return { success: true };
  } catch (error: unknown) {
    const message =
      error instanceof Error ? error.message : 'Failed to save rules';
    useProxyStore.getState().setMatchReplacePersistStatus({
      status: 'error',
      error: message,
    });
    console.warn('Failed to persist match & replace rules', {
      error: error instanceof Error ? error : new Error(String(error)),
    });
    return { success: false, error: message };
  }
}

export async function persistCurrentMatchReplaceRulesToServer({
  applicationId: targetApplicationId,
  skipSelectedApplicationCheck = false,
}: {
  applicationId?: string;
  skipSelectedApplicationCheck?: boolean;
} = {}): Promise<{ success: true } | { success: false; error: string }> {
  const applicationId =
    targetApplicationId ??
    useProjectStore.getState().selectedTarget?.id ??
    useProjectStore.getState().targetId;
  const { settings } = useProxyStore.getState();
  const snapshot = captureMatchReplacePersistSnapshot({
    applicationId,
    rules: settings.matchReplaceRules ?? [],
    matchReplaceEnabled: settings.matchReplaceEnabled,
  });

  if (!snapshot) {
    return {
      success: false,
      error: 'No target selected for saving match/replace rules',
    };
  }

  return persistMatchReplaceSnapshotToServer(snapshot, {
    skipSelectedApplicationCheck,
  });
}
